import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsSankey from 'highcharts/modules/sankey';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { journey } from '../../../../data/journeyData';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { chartColors } from 'views/Utils/ChartColors';

HighchartsSankey(Highcharts);

const Sankey = () => {
  const [journeyData, setJourneyData] = useState([]);
  const [countsData, setCountsData] = useState([]);
  const [departmentColors, setDepartmentColors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);

  const currentDate = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);
  const source = JSON.parse(localStorage.getItem('source'));

  // Fetch department colors from API
  useEffect(() => {
    const fetchDepartmentColors = async () => {
      try {
        const response = await axios.post('https://db.neophyte.live/sentinel-api/api/sentinel/getDepartments', {
          source_id: source
        });

        if (response.data && response.data.data) {
          setDepartmentColors(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching department colors:', error);
      }
    };

    if (source) {
      fetchDepartmentColors();
    }
  }, [source]);


  const formatApiDate = (date) => {
    if (!date) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }

    try {
      const dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Error formatting date:', e);
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      return date;
    }

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const apiDate = formatApiDate(currentDate);
  const displayDate = formatDisplayDate(currentDate);

  // Fetch journey data from API
  useEffect(() => {
    if (!source) {
      console.error('Source ID is missing');
      setError('Source ID is missing. Please select a store first.');
      setIsLoading(false);
      return;
    }

    if (displayDate === '29-4-25') {
      setJourneyData(journey);
      setIsDataAvailable(true);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchJourneyData = async () => {
      setIsLoading(true);
      setError(null);

      const dateToUse = apiDate || formatApiDate(new Date());
      const startTime = new Date(selectedTime.start_time).toUTCString();
      const endTime = new Date(selectedTime.end_time).toUTCString();

      try {
        const requestData = {
          date: dateToUse,
          source_id: source,
          start_time: startTime,
          end_time: endTime
        };

        const response = await axios.post('https://db.neophyte.live/sentinel-api/api/sentinel/journeyChart', requestData);
        // const response = await axios.post('http://localhost:7072/api/sentinel/journeyChart', requestData);

        if (response.data && response.data.data && response.data.data[0]) {
          const apiData = response.data.data[0].mapping || [];
          const counts = response.data.data[0].counts || [];

          console.log('Journey data received:', apiData);
          setJourneyData(apiData);
          setCountsData(counts);
          setIsDataAvailable(apiData.length > 0 || counts.length > 0);
        } else {
          console.log('No journey data available in response');
          setJourneyData([]);
          setCountsData([]);
          setIsDataAvailable(false);
        }
      } catch (error) {
        console.error('Error fetching journey data:', error);
        setError('Failed to fetch journey data');
        setJourneyData([]);
        setCountsData([]);
        setIsDataAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJourneyData();
  }, [currentDate, apiDate, source, displayDate, selectedTime?.start_time_string, selectedTime?.end_time_string]);

  const getChartOptions = () => {
    // Process data to extract unique departments and assign consistent colors
    const departmentMap = {};
    const colorMap = {};

    // Set fixed colors for Entry and Exit
    colorMap['Entry'] = '#90ed7d'; // Green color for Entry
    colorMap['Exit'] = '#f45b5b'; // Red color for Exit

    // Apply colors from the departmentColors API response for other departments
    departmentColors.forEach((dept) => {
      colorMap[dept.name] = dept.color;
    });

    // Extract all unique department names (without level numbers)
    journeyData.forEach((link) => {
      // Extract the base department name by removing any trailing numbers
      const fromBase = link[0].replace(/\d+$/, '');
      const toBase = link[1].replace(/\d+$/, '');

      // Store the original node name with its base department name
      departmentMap[link[0]] = fromBase;
      departmentMap[link[1]] = toBase;

      // Add base department names to track unique departments
      if (!colorMap[fromBase]) {
        // If department color not in API response, use a default color
        colorMap[fromBase] = chartColors[Math.floor(Math.random() * chartColors.length)];
      }
      if (!colorMap[toBase]) {
        colorMap[toBase] = chartColors[Math.floor(Math.random() * chartColors.length)];
      }
    });

    // Create nodes with their original names but assign colors based on their base department
    const nodes = [];
    const processedNodeNames = new Set();

    // Process data while preserving original node names for the chart structure
    const processedData = journeyData.map((link) => {
      const from = link[0];
      const to = link[1];
      const value = link[2];

      // Add nodes with consistent colors based on department
      if (!processedNodeNames.has(from)) {
        nodes.push({
          id: from,
          // Display name without numbers for labels
          name: departmentMap[from],
          color: colorMap[departmentMap[from]]
        });
        processedNodeNames.add(from);
      }

      if (!processedNodeNames.has(to)) {
        nodes.push({
          id: to,
          // Display name without numbers for labels
          name: departmentMap[to],
          color: colorMap[departmentMap[to]]
        });
        processedNodeNames.add(to);
      }

      return [from, to, value];
    });

    // Create legend items for unique departments
    const legendItems = Object.keys(colorMap).map((dept) => ({
      name: dept,
      color: colorMap[dept]
    }));

    return {
      chart: {
        type: 'sankey',
        height: 450,
        marginTop: 100, // Increased to make room for the legend
        backgroundColor: 'transparent'
      },
      title: {
        text: 'Customer Journey Flow',
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#333'
        }
      },
      subtitle: {
        text: 'Flow of customers between different departments',
        align: 'left',
        style: {
          color: '#666',
          fontSize: '12px'
        }
      },
      tooltip: {
        headerFormat: '<span style="font-size: 13px">{series.name}</span><br/>',
        formatter: function () {
          // Check if this is a node or a link
          if (this.point.linksTo || this.point.linksFrom) {
            // This is a node - show total customers for this node
            let totalCustomers = 0;

            // Sum incoming links (customers coming to this node)
            if (this.point.linksTo) {
              this.point.linksTo.forEach((link) => {
                totalCustomers += link.weight;
              });
            }

            // If there are no incoming links, use outgoing links
            // This handles the case of starting nodes that only have outgoing links
            if (totalCustomers === 0 && this.point.linksFrom) {
              this.point.linksFrom.forEach((link) => {
                totalCustomers += link.weight;
              });
            }

            return `<b>${this.point.name}</b><br/>Total Customers: <b>${totalCustomers}</b>`;
          } else {
            // This is a link between nodes - show the flow information
            const fromNode = this.point.fromNode;
            const toNode = this.point.toNode;
            return `<b>${fromNode.name}</b> → <b>${toNode.name}</b><br/>Customers: <b>${this.point.weight}</b>`;
          }
        },
        style: {
          fontSize: '12px'
        }
      },
      plotOptions: {
        sankey: {
          dataLabels: {
            enabled: true,
            crop: false,
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              fontSize: '11px'
            },
            nodeFormat: '{point.name}'
          },
          node: {
            width: 12,
            nodeWidth: 12,
            colorByPoint: true,
            dataLabels: {
              style: {
                color: 'rgba(0, 0, 0, 0.8)',
                textOutline: 'none',
                fontWeight: 'normal'
              }
            }
          }
        }
      },
      series: [
        // Main sankey chart series
        {
          keys: ['from', 'to', 'weight'],
          data: processedData,
          type: 'sankey',
          name: 'Customer Journey',
          nodes: nodes,
          dataLabels: {
            enabled: true
          }
        },
        // Add a dummy series for the legend
        {
          type: 'pie',
          name: 'Departments',
          data: legendItems,
          showInLegend: true,
          enableMouseTracking: false,
          legendType: 'point',
          visible: false // Hide the actual pie chart
        }
      ],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'top',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderWidth: 1,
        borderColor: '#e6e6e6',
        borderRadius: 5,
        padding: 10,
        margin: 10,
        itemStyle: {
          fontWeight: 'normal',
          fontSize: '12px',
          cursor: 'default' // Prevent cursor from changing to pointer
        },
        symbolRadius: 0,
        useHTML: true,
        labelFormatter: function () {
          return `<span style="color:${this.color}">\u25CF</span> ${this.name}`;
        },
        itemMarginTop: 5,
        itemMarginBottom: 5,
        itemMarginLeft: 10,
        itemMarginRight: 10
      }
    };
  };

  const renderToggleButton = () => {
    if (!isDataAvailable || countsData.length === 0) return null;

    return (
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ToggleButtonGroup
          color="primary"
          value={showTable ? 'table' : 'chart'}
          exclusive
          onChange={(e, newView) => {
            if (newView !== null) {
              setShowTable(newView === 'table');
            }
          }}
          aria-label="view mode"
        >
          <ToggleButton value="chart" aria-label="sankey chart">
            <BarChartIcon sx={{ mr: 1 }} />
            Chart
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChartIcon sx={{ mr: 1 }} />
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  };

  const renderTable = () => {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Customer Journey Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Breakdown of customer visits across different department combinations
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <Table aria-label="journey counts table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>Departments Visited</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Customers
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {countsData.map((row, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'action.hover'
                    },
                    '&:last-child td': {
                      borderBottom: 0
                    }
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight: 'medium',
                      borderRight: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      maxWidth: '300px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.departments_covered}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      color: 'primary.main',
                      fontSize: '1.1em'
                    }}
                  >
                    {row.count.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {countsData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
            Showing {countsData.length} department combinations
          </Typography>
        )}
      </Box>
    );
  };

  const renderChart = () => {
    return (
      <Box>
        <div className="h-[450px]">
          <HighchartsReact highcharts={Highcharts} options={getChartOptions()} />
        </div>
        <div style={{ background: 'white', width: '80px', height: '15px', position: 'absolute', right: '0', bottom: '10px' }}></div>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <div
        className="chart-loading-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderLeftColor: '#44BE54',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}
        ></div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div style={{ fontWeight: '500', color: '#555' }}>Loading Journey Data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#dc3545', fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  if (!currentDate) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#6c757d', fontSize: '16px' }}>Please select a date to view customer journey</p>
      </div>
    );
  }

  if (!isDataAvailable) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <p style={{ color: '#6c757d', fontSize: '16px', marginBottom: '8px' }}>No customer journey data available for {displayDate}</p>
      </div>
    );
  }

  return (
    <Box>
      {renderToggleButton()}
      {showTable ? renderTable() : renderChart()}
    </Box>
  );
};

export default Sankey;
